/**
 * GameScene — 무한의 계단 스타일 핵심 게임 플레이
 */
import Phaser from 'phaser';
import {
  GAME_WIDTH, GAME_HEIGHT,
  CLOUD, PLAYER, GAUGE, COMBO, STAGE,
  CloudType, CLOUD_WEIGHTS,
} from '../config';
import { submitScore, getPlayerId } from '../api';

/* ────── 타입 ────── */
interface CloudData {
  type: CloudType;
  worldX: number;
  worldY: number;
  sprite: Phaser.GameObjects.Image;
}

type StageKey = 'DAY' | 'SUNSET' | 'NIGHT' | 'SPACE';

const BG_KEYS: Record<StageKey, string> = {
  DAY: 'bg_day',
  SUNSET: 'bg_sunset',
  NIGHT: 'bg_night',
  SPACE: 'bg_space',
};

const CLOUD_SCALE = 0.16;
const PLAYER_SCALE = 0.12;

export class GameScene extends Phaser.Scene {
  /* 게임 상태 */
  private score = 0;
  private combo = 0;
  private bestCombo = 0;
  private stepCount = 0;
  private isGameOver = false;
  private isFever = false;
  private isStunned = false;
  private isJumping = false;
  
  // 플레이어 방향 (1: 오른쪽, -1: 왼쪽)
  private playerDir = 1;

  /* 타이밍 게이지 */
  private gauge = GAUGE.INITIAL_TIME;
  private gaugeMax = GAUGE.INITIAL_TIME;
  private gaugeTimer = 0;

  /* 구름 관련 */
  private clouds: CloudData[] = [];
  private currentCloudIndex = 0;

  /* 그래픽 객체 */
  private player!: Phaser.GameObjects.Sprite;
  private bgImage!: Phaser.GameObjects.Image;
  private bgImageNext!: Phaser.GameObjects.Image;
  private gaugeBar!: Phaser.GameObjects.Graphics;
  private gaugeBg!: Phaser.GameObjects.Graphics;
  private scoreText!: Phaser.GameObjects.Text;
  private comboText!: Phaser.GameObjects.Text;
  private stageText!: Phaser.GameObjects.Text;
  private heightText!: Phaser.GameObjects.Text;
  private feverText!: Phaser.GameObjects.Text;

  /* 카메라 오프셋 */
  private cameraX = 0;
  private cameraY = 0;
  private targetCameraX = 0;
  private targetCameraY = 0;

  /* 현재 스테이지 */
  private currentStage: StageKey = 'DAY';

  constructor() {
    super({ key: 'GameScene' });
  }

  create(): void {
    this.resetState();

    // ── 배경 ──
    this.bgImage = this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, 'bg_day').setDisplaySize(GAME_WIDTH, GAME_HEIGHT);
    this.bgImageNext = this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, 'bg_sunset').setDisplaySize(GAME_WIDTH, GAME_HEIGHT).setAlpha(0);

    // ── 구름 생성 ──
    this.generateInitialClouds();

    // ── 플레이어 ──
    const startCloud = this.clouds[0];
    this.player = this.add.sprite(startCloud.worldX, startCloud.worldY - 20, 'player_idle');
    this.player.setScale(PLAYER_SCALE * 2.5).setOrigin(0.5, 1).setDepth(10).play('player_idle_anim');
    this.playerDir = 1; // 기본 우측

    // ── HUD ──
    this.createHUD();

    // ── 컨트롤 버튼 ──
    this.createControlButtons();

    // ── 입력 ──
    this.setupKeyboard();

    this.cameras.main.fadeIn(400);
  }

  update(_time: number, delta: number): void {
    if (this.isGameOver) return;

    // 게이지 감소
    if (!this.isFever) {
      this.gaugeTimer += delta;
      this.gauge = Math.max(0, this.gaugeMax - this.gaugeTimer);
      if (this.gauge <= 0) { this.triggerGameOver(); return; }
    }

    // 카메라 이동
    this.cameraX += (this.targetCameraX - this.cameraX) * 0.1;
    this.cameraY += (this.targetCameraY - this.cameraY) * 0.1;

    // 객체 위치 업데이트
    this.updateCloudPositions();
    this.updatePlayerPosition();
    this.updateHUD();
  }

  private resetState(): void {
    this.score = 0; this.combo = 0; this.bestCombo = 0; this.stepCount = 0;
    this.isGameOver = false; this.isFever = false; this.isStunned = false; this.isJumping = false;
    this.playerDir = 1; this.gauge = GAUGE.INITIAL_TIME; this.gaugeMax = GAUGE.INITIAL_TIME; this.gaugeTimer = 0;
    this.clouds = []; this.currentCloudIndex = 0;
    this.cameraX = 0; this.cameraY = 0; this.targetCameraX = 0; this.targetCameraY = 0;
    this.currentStage = 'DAY';
  }

  /* ═══════════ 구름 생성 (계단) ═══════════ */
  private generateInitialClouds(): void {
    // 첫 계단: 중앙 하단
    this.addCloud(GAME_WIDTH / 2, GAME_HEIGHT * 0.75, CloudType.NORMAL);
    for (let i = 1; i < 30; i++) {
        this.addNextCloud();
    }
  }

  private addNextCloud(): void {
    const prev = this.clouds[this.clouds.length - 1];
    const dir = Math.random() < 0.5 ? 1 : -1; // 랜덤 방향 전환 포인트
    const worldX = prev.worldX + (dir * CLOUD.HORIZ_OFFSET);
    const worldY = prev.worldY - CLOUD.BASE_GAP;
    this.addCloud(worldX, worldY, this.rollCloudType());
  }

  private addCloud(worldX: number, worldY: number, type: CloudType): void {
    const sprite = this.add.image(worldX, worldY, `cloud_${type}`).setScale(CLOUD_SCALE).setDepth(2);
    this.clouds.push({ type, worldX, worldY, sprite });
  }

  private updateCloudPositions(): void {
    this.clouds.forEach(c => {
      c.sprite.x = c.worldX - this.cameraX;
      c.sprite.y = c.worldY - this.cameraY;
      c.sprite.visible = c.sprite.y > -100 && c.sprite.y < GAME_HEIGHT + 100;
    });
  }

  private updatePlayerPosition(): void {
    const curCloud = this.clouds[this.currentCloudIndex];
    if (!this.isJumping) {
      this.player.x = curCloud.worldX - this.cameraX;
      this.player.y = curCloud.worldY - this.cameraY - 10;
    }
  }

  /* ═══════════ 게임 로직 ═══════════ */
  private handleAction(isTurn: boolean): void {
    if (this.isGameOver || this.isStunned || this.isJumping) return;

    if (isTurn) this.playerDir *= -1;
    
    this.player.setFlipX(this.playerDir === -1);

    const nextCloud = this.clouds[this.currentCloudIndex + 1];
    const expectedX = this.clouds[this.currentCloudIndex].worldX + (this.playerDir * CLOUD.HORIZ_OFFSET);

    // 죽음 판정: 다음 계단 위치가 내 진행 방향과 맞지 않으면
    if (Math.abs(nextCloud.worldX - expectedX) > 5) {
      this.triggerGameOver();
      return;
    }

    this.jumpToNext();
  }

  private jumpToNext(): void {
    this.isJumping = true;
    this.currentCloudIndex++;
    this.stepCount++;
    this.player.play('player_jump_anim');

    const targetCloud = this.clouds[this.currentCloudIndex];
    const targetX = targetCloud.worldX - this.cameraX;
    const targetY = targetCloud.worldY - this.cameraY - 10;

    // 점프 트윈
    this.tweens.add({
      targets: this.player,
      x: targetX,
      y: targetY - 30, // 포물선
      duration: PLAYER.JUMP_DURATION / 2,
      yoyo: true,
      ease: 'Quad.easeOut',
      onComplete: () => {
        this.isJumping = false;
        this.player.play('player_idle_anim');
        this.onStepComplete(targetCloud);
      }
    });

    // 카메라 팔로우 타겟
    this.targetCameraX = targetCloud.worldX - GAME_WIDTH / 2;
    this.targetCameraY = targetCloud.worldY - GAME_HEIGHT * 0.65;
    
    if (this.clouds.length - this.currentCloudIndex < 15) {
      for(let i=0; i<10; i++) this.addNextCloud();
    }
  }

  private onStepComplete(cloud: CloudData): void {
    this.score += 10 + (this.isFever ? 40 : 0);
    this.combo++;
    if (this.combo > this.bestCombo) this.bestCombo = this.combo;

    this.gaugeTimer = 0;
    this.gaugeMax = Math.max(GAUGE.MIN_TIME, GAUGE.INITIAL_TIME - this.stepCount * 3);
    this.gauge = this.gaugeMax;

    if (cloud.type === CloudType.LIGHTNING) this.triggerStun();
    if (this.combo >= COMBO.FEVER_THRESHOLD && !this.isFever) this.triggerFever();
    
    this.checkStageTransition();
  }

  /* ═══════════ UI & HUD ═══════════ */
  private createControlButtons(): void {
    const bottomY = GAME_HEIGHT - 120;
    const btnW = 140;
    const btnH = 100;

    // 왼쪽 버튼 (방향 전환)
    this.createGlassBtn(GAME_WIDTH * 0.28, bottomY, btnW, btnH, '🔄', '방향전환', 0xAA77DD, () => this.handleAction(true));
    // 오른쪽 버튼 (오르기)
    this.createGlassBtn(GAME_WIDTH * 0.72, bottomY, btnW, btnH, '👟', '오르기', 0x77AADD, () => this.handleAction(false));
  }

  private createGlassBtn(x: number, y: number, w: number, h: number, emoji: string, label: string, color: number, callback: () => void): void {
    const container = this.add.container(x, y).setDepth(100);
    
    const bg = this.add.graphics();
    // 버튼 배경
    bg.fillStyle(color, 0.4);
    bg.fillRoundedRect(-w/2, -h/2, w, h, 28);
    // 상단 광택
    bg.fillStyle(0xFFFFFF, 0.15);
    bg.fillRoundedRect(-w/2 + 4, -h/2 + 4, w - 8, h/2 - 4, { tl: 24, tr: 24, bl: 0, br: 0 });
    // 테두리
    bg.lineStyle(2.5, 0xFFFFFF, 0.4);
    bg.strokeRoundedRect(-w/2, -h/2, w, h, 28);

    const emojiTxt = this.add.text(0, -12, emoji, { fontSize: '40px' }).setOrigin(0.5);
    const labelTxt = this.add.text(0, 24, label, { 
      fontFamily: '"Noto Sans KR", sans-serif',
      fontSize: '15px', 
      fontStyle: 'bold', 
      color: '#FFFFFF' 
    }).setOrigin(0.5);
    
    container.add([bg, emojiTxt, labelTxt]);

    const hit = this.add.rectangle(0, 0, w, h).setInteractive({ useHandCursor: true });
    container.add(hit);

    hit.on('pointerdown', () => {
      this.tweens.add({
        targets: container,
        scale: 0.9,
        duration: 80,
        yoyo: true,
        onComplete: callback
      });
    });
  }

  private createHUD(): void {
    const d = 50;
    this.scoreText = this.add.text(20, 20, '0', { fontSize: '24px', fontStyle: 'bold', color: '#FFF' }).setDepth(d);
    this.comboText = this.add.text(GAME_WIDTH - 20, 20, '0 COMBO', { fontSize: '20px', fontStyle: 'bold', color: '#FFD700' }).setOrigin(1, 0).setDepth(d);
    this.heightText = this.add.text(20, 50, '0m', { fontSize: '14px', color: '#AAF' }).setDepth(d);

    this.gaugeBg = this.add.graphics().setDepth(d).fillStyle(0x000, 0.3).fillRoundedRect(20, 80, GAME_WIDTH - 40, 10, 5);
    this.gaugeBar = this.add.graphics().setDepth(d+1);
  }

  private updateHUD(): void {
    this.scoreText.setText(this.score.toLocaleString());
    this.comboText.setText(`${this.combo} COMBO`);
    this.heightText.setText(`${this.stepCount * 5}m`);

    const ratio = this.gauge / this.gaugeMax;
    this.gaugeBar.clear();
    this.gaugeBar.fillStyle(ratio > 0.3 ? 0x88DDFF : 0xFF6688, 0.8);
    this.gaugeBar.fillRoundedRect(20, 80, (GAME_WIDTH - 40) * ratio, 10, 5);
  }

  /* ═══════════ 기타 유틸 ═══════════ */
  private setupKeyboard(): void {
    this.input.keyboard?.on('keydown-Z', () => this.handleAction(true));
    this.input.keyboard?.on('keydown-X', () => this.handleAction(false));
  }

  private rollCloudType(): CloudType {
    const rand = Math.random();
    let cumulative = 0;
    for (const [type, weight] of Object.entries(CLOUD_WEIGHTS)) {
      cumulative += weight;
      if (rand <= cumulative) return type as CloudType;
    }
    return CloudType.NORMAL;
  }

  private triggerGameOver(): void {
    if (this.isGameOver) return;
    this.isGameOver = true;
    this.cameras.main.shake(300, 0.015);
    
    this.time.delayedCall(600, () => {
      this.showGameOver();
    });
  }

  private showGameOver(): void {
    const overlay = this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x000, 0.7).setDepth(200);
    overlay.setInteractive();

    const container = this.add.container(GAME_WIDTH / 2, GAME_HEIGHT / 2).setDepth(201);
    
    const panel = this.add.graphics();
    panel.fillStyle(0x222244, 0.9);
    panel.fillRoundedRect(-160, -220, 320, 440, 30);
    panel.lineStyle(3, 0xFFFFFF, 0.3);
    panel.strokeRoundedRect(-160, -220, 320, 440, 30);

    const title = this.add.text(0, -180, 'GAME OVER', {
      fontFamily: '"Outfit", sans-serif',
      fontSize: '36px',
      fontStyle: 'bold',
      color: '#FF6688',
    }).setOrigin(0.5);

    const scoreLabel = this.add.text(0, -120, 'YOUR SCORE', { fontSize: '14px', color: '#DDEEFF' }).setOrigin(0.5);
    const scoreVal = this.add.text(0, -90, this.score.toLocaleString(), { fontSize: '48px', fontStyle: 'bold', color: '#FFFFFF' }).setOrigin(0.5);

    const savedName = localStorage.getItem('sky_walker_nickname') || '';
    const nameLabel = this.add.text(0, -20, 'NICKNAME', { fontSize: '13px', color: '#AABBDD' }).setOrigin(0.5);
    
    const nameInputBg = this.add.graphics();
    nameInputBg.fillStyle(0x000000, 0.3);
    nameInputBg.fillRoundedRect(-120, -5, 240, 40, 10);
    nameInputBg.lineStyle(1.5, 0xFFFFFF, 0.2);
    nameInputBg.strokeRoundedRect(-120, -5, 240, 40, 10);

    const nameText = this.add.text(0, 15, savedName || 'Touch to input', { 
      fontSize: '18px', 
      color: savedName ? '#FFF' : '#667',
      align: 'center'
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });

    nameText.on('pointerdown', () => {
      const input = window.prompt('닉네임을 입력하세요 (최대 10자)', nameText.text === 'Touch to input' ? '' : nameText.text);
      if (input !== null && input.trim() !== '') {
        const sliced = input.trim().slice(0, 10);
        nameText.setText(sliced).setColor('#FFF');
        localStorage.setItem('sky_walker_nickname', sliced);
      }
    });

    const registerBtn = this.createPopupBtn(0, 80, 'REGISTER SCORE', 0xFFD700, async () => {
      const finalName = nameText.text;
      if (finalName === 'Touch to input' || !finalName) {
        alert('닉네임을 입력해주세요!');
        return;
      }
      try {
        await submitScore({ 
            score: this.score, 
            best_combo: this.bestCombo, 
            max_stage: this.currentStage, 
            step_count: this.stepCount,
            nickname: finalName 
        });
        alert('랭킹에 등록되었습니다! ✨');
        registerBtn.destroy();
      } catch (e) {
        alert('등록에 실패했습니다.');
      }
    });

    const retryBtn = this.createPopupBtn(0, 140, 'RETRY', 0x88DDFF, () => this.scene.restart());
    const homeBtn = this.createPopupBtn(0, 195, 'HOME', 0xAAAAAA, () => this.scene.start('MenuScene'));

    container.add([panel, title, scoreLabel, scoreVal, nameLabel, nameInputBg, nameText, registerBtn, retryBtn, homeBtn]);
    
    container.setScale(0.5);
    container.setAlpha(0);
    this.tweens.add({
      targets: container,
      scale: 1,
      alpha: 1,
      duration: 300,
      ease: 'Back.easeOut'
    });
  }

  private createPopupBtn(x: number, y: number, label: string, color: number, cb: () => void): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);
    const bg = this.add.graphics();
    bg.fillStyle(color, 0.8);
    bg.fillRoundedRect(-110, -22, 220, 44, 15);
    
    const txt = this.add.text(0, 0, label, { 
        fontSize: '16px', 
        fontStyle: 'bold', 
        color: color === 0xFFD700 ? '#224' : '#FFF' 
    }).setOrigin(0.5);
    
    container.add([bg, txt]);
    
    const hit = this.add.rectangle(0, 0, 220, 44).setInteractive({ useHandCursor: true });
    container.add(hit);
    
    hit.on('pointerdown', () => {
        container.setScale(0.95);
        cb();
    });
    hit.on('pointerup', () => container.setScale(1));
    hit.on('pointerout', () => container.setScale(1));
    
    return container;
  }

  private triggerFever(): void {
    this.isFever = true;
    this.time.delayedCall(COMBO.FEVER_DURATION, () => this.isFever = false);
  }

  private triggerStun(): void {
    this.isStunned = true;
    this.player.setTint(0xFFFF00);
    this.time.delayedCall(500, () => { this.isStunned = false; this.player.clearTint(); });
  }

  private checkStageTransition(): void {
    let newStage: StageKey;
    if (this.stepCount <= STAGE.DAY_END) newStage = 'DAY';
    else if (this.stepCount <= STAGE.SUNSET_END) newStage = 'SUNSET';
    else if (this.stepCount <= STAGE.NIGHT_END) newStage = 'NIGHT';
    else newStage = 'SPACE';

    if (newStage !== this.currentStage) {
      this.transitionBackground(newStage);
      this.currentStage = newStage;
      this.stageText.setText(this.getStageName(newStage));
    }
  }

  private transitionBackground(newStage: StageKey): void {
    this.bgImageNext.setTexture(BG_KEYS[newStage]);
    this.bgImageNext.setAlpha(0);

    this.tweens.add({
      targets: this.bgImageNext,
      alpha: 1,
      duration: 1500,
      onComplete: () => {
        this.bgImage.setTexture(BG_KEYS[newStage]);
        this.bgImageNext.setAlpha(0);
      }
    });

    this.cameras.main.flash(400, 255, 255, 255);
  }

  private getStageName(stage: StageKey): string {
    switch (stage) {
      case 'DAY': return '🌤 Blue Sky';
      case 'SUNSET': return '🌅 Sunset';
      case 'NIGHT': return '🌙 Night';
      case 'SPACE': return '🌌 Space';
      default: return '';
    }
  }
}


